import { useSuspenseQuery } from '@tanstack/react-query';
import { appQueries } from '../../api/app.api';
import { useCallback, useMemo } from 'react';
import { Owner } from '../../api/graph.types';
import useLicenseInfo from './useLicenseInfo';

const millisecondsInADay = 24 * 3600 * 1000;

export default function useExpiryClassification() {
  const { data: isAppEnabledResponse } = useSuspenseQuery(appQueries.isAppEnabled);
  const { isProfessional } = useLicenseInfo();

  const nowTime = new Date().getTime();
  const expirationThresholdDays = isProfessional ? isAppEnabledResponse.settings.expirationThresholdDays : 30;
  const samlExpirationThresholdDays = isProfessional ? isAppEnabledResponse.settings.samlExpirationThresholdDays : 60;

  const regularExpiryTime = nowTime + expirationThresholdDays * millisecondsInADay;
  const samlExpiryTime = nowTime + samlExpirationThresholdDays * millisecondsInADay;

  const managedAppIds = useMemo(() => {
    return new Set(isAppEnabledResponse?.managedAppInfo.managedAppIds);
  }, [isAppEnabledResponse]);

  const unmanagedAppIds = useMemo(() => {
    return new Set(isAppEnabledResponse?.managedAppInfo.unmanagedAppIds);
  }, [isAppEnabledResponse]);

  const managedAppOwnerIds = useMemo(() => {
    return new Set(isAppEnabledResponse?.managedAppInfo.managedAppOwnerIds);
  }, [isAppEnabledResponse]);

  const isAboutToExpire = useCallback(
    (date: Date | undefined, isSaml: boolean) => {
      if (!date) return false;

      const endTime = date.getTime();
      const nowTime = new Date().getTime();
      return nowTime < endTime && endTime < (isSaml ? samlExpiryTime : regularExpiryTime);
    },
    [regularExpiryTime, samlExpiryTime]
  );

  const isExpired = useCallback((date: Date | undefined) => {
    if (!date) return false;

    const endTime = date.getTime();
    const nowTime = new Date().getTime();
    return endTime < nowTime;
  }, []);

  const isManaged = useCallback(
    (appObjectId: string, owners?: Owner[]) => {
      if (!isProfessional) return false;

      if (managedAppIds.has(appObjectId)) return true;

      if (unmanagedAppIds.has(appObjectId)) return false;

      if (!owners) return false;

      for (const owner of owners) {
        if (managedAppOwnerIds.has(owner.id) || (owner.appId && managedAppOwnerIds.has(owner.appId))) return true;
      }

      return false;
    },
    [managedAppIds, unmanagedAppIds, managedAppOwnerIds, isProfessional]
  );

  return {
    expirationThresholdDays,
    isAboutToExpire,
    isExpired,
    isManaged,
  };
}
